import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import productData from "../Datas/productData";

const CART_STORAGE_KEY = "cartItems";
const WISHLIST_STORAGE_KEY = "wishlistItems";
const MAX_QUANTITY = 99;

export const CartContext = createContext(null);

const readStorage = (key, fallbackValue) => {
  if (typeof window === "undefined" || !window.localStorage) {
    return fallbackValue;
  }

  try {
    const storedValue = window.localStorage.getItem(key);
    return storedValue === null ? fallbackValue : JSON.parse(storedValue);
  } catch {
    return fallbackValue;
  }
};

const writeStorage = (key, value) => {
  if (typeof window === "undefined" || !window.localStorage) return;

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage can be unavailable in private browsing or when the quota is full.
  }
};

const normalizeProductId = (value) => {
  const candidate =
    value && typeof value === "object" ? value.id ?? value.productId : value;

  if (typeof candidate === "number" && Number.isFinite(candidate)) {
    return candidate;
  }

  if (typeof candidate === "string" && candidate.trim()) {
    const trimmed = candidate.trim();
    return /^\d+$/.test(trimmed) ? Number(trimmed) : trimmed;
  }

  return null;
};

const normalizeOption = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") {
    return String(value.name ?? value.label ?? value.value ?? "").trim();
  }
  return String(value).trim();
};

const normalizeQuantity = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 1;
  return Math.min(MAX_QUANTITY, Math.max(1, Math.floor(parsed)));
};

const isSameVariant = (item, productId, size, color) =>
  String(item.id) === String(productId) &&
  item.size === size &&
  item.color === color;

const cleanCartItems = (value) => {
  if (!Array.isArray(value)) return [];

  return value.reduce((items, candidate) => {
    if (!candidate || typeof candidate !== "object") return items;

    const id = normalizeProductId(candidate.id ?? candidate.productId);
    if (id === null) return items;

    const size = normalizeOption(candidate.size);
    const color = normalizeOption(candidate.color);
    const quantity = normalizeQuantity(candidate.quantity);
    const existingIndex = items.findIndex((item) =>
      isSameVariant(item, id, size, color)
    );

    if (existingIndex >= 0) {
      const existing = items[existingIndex];
      items[existingIndex] = {
        ...existing,
        quantity: Math.min(MAX_QUANTITY, existing.quantity + quantity),
      };
      return items;
    }

    items.push({ id, size, color, quantity });
    return items;
  }, []);
};

const cleanWishlist = (value) => {
  if (!Array.isArray(value)) return [];

  return value.reduce((items, candidate) => {
    const id = normalizeProductId(candidate);
    if (id !== null && !items.some((item) => String(item) === String(id))) {
      items.push(id);
    }
    return items;
  }, []);
};

const getProductPrice = (product) => {
  if (!product) return 0;
  const value =
    typeof product.price === "string"
      ? product.price.replace(/[^0-9.-]/g, "")
      : product.price;
  const price = Number(value);
  return Number.isFinite(price) ? price : 0;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() =>
    cleanCartItems(readStorage(CART_STORAGE_KEY, []))
  );
  const [wishlist, setWishlist] = useState(() => {
    const savedWishlist = readStorage(WISHLIST_STORAGE_KEY, null);
    const legacyWishlist = readStorage("wishlist", []);
    return cleanWishlist(savedWishlist ?? legacyWishlist);
  });

  useEffect(() => {
    writeStorage(CART_STORAGE_KEY, cleanCartItems(cartItems));
  }, [cartItems]);

  useEffect(() => {
    writeStorage(WISHLIST_STORAGE_KEY, cleanWishlist(wishlist));
  }, [wishlist]);

  const addToCart = useCallback(
    (productId, quantity = 1, size = "", color = "") => {
      const payload =
        productId && typeof productId === "object" ? productId : null;
      const id = normalizeProductId(productId);
      if (id === null) return;

      const nextQuantity = normalizeQuantity(payload?.quantity ?? quantity);
      const nextSize = normalizeOption(
        payload?.size ?? payload?.selectedSize ?? size
      );
      const nextColor = normalizeOption(
        payload?.color ?? payload?.selectedColor ?? color
      );

      setCartItems((previousItems) => {
        const existingItem = previousItems.find((item) =>
          isSameVariant(item, id, nextSize, nextColor)
        );

        if (existingItem) {
          return previousItems.map((item) =>
            isSameVariant(item, id, nextSize, nextColor)
              ? {
                  ...item,
                  quantity: Math.min(
                    MAX_QUANTITY,
                    item.quantity + nextQuantity
                  ),
                }
              : item
          );
        }

        return [
          ...previousItems,
          { id, size: nextSize, color: nextColor, quantity: nextQuantity },
        ];
      });
    },
    []
  );

  const removeFromCart = useCallback((productId, size = "", color = "") => {
    const id = normalizeProductId(productId);
    if (id === null) return;

    const nextSize = normalizeOption(size);
    const nextColor = normalizeOption(color);
    setCartItems((previousItems) =>
      previousItems.filter(
        (item) => !isSameVariant(item, id, nextSize, nextColor)
      )
    );
  }, []);

  const updateQuantity = useCallback(
    (productId, size = "", color = "", quantity = 1) => {
      const id = normalizeProductId(productId);
      if (id === null) return;

      const nextSize = normalizeOption(size);
      const nextColor = normalizeOption(color);
      const nextQuantity = normalizeQuantity(quantity);
      setCartItems((previousItems) =>
        previousItems.map((item) =>
          isSameVariant(item, id, nextSize, nextColor)
            ? { ...item, quantity: nextQuantity }
            : item
        )
      );
    },
    []
  );

  const clearCart = useCallback(() => setCartItems([]), []);

  const toggleWishlist = useCallback((productId) => {
    const id = normalizeProductId(productId);
    if (id === null) return;

    setWishlist((previousItems) =>
      previousItems.some((item) => String(item) === String(id))
        ? previousItems.filter((item) => String(item) !== String(id))
        : [...previousItems, id]
    );
  }, []);

  const isWishlisted = useCallback(
    (productId) => {
      const id = normalizeProductId(productId);
      return (
        id !== null &&
        wishlist.some((item) => String(item) === String(id))
      );
    },
    [wishlist]
  );

  const cartCount = useMemo(
    () => cartItems.reduce((count, item) => count + item.quantity, 0),
    [cartItems]
  );

  const subtotal = useMemo(() => {
    const productsById = new Map(
      productData.map((product) => [String(product.id), product])
    );

    return cartItems.reduce((total, item) => {
      const product = productsById.get(String(item.id));
      return total + getProductPrice(product) * item.quantity;
    }, 0);
  }, [cartItems]);

  const contextValue = useMemo(
    () => ({
      cartItems,
      wishlist,
      cartCount,
      subtotal,
      addToCart,
      addCart: addToCart,
      removeFromCart,
      removeCart: removeFromCart,
      updateQuantity,
      clearCart,
      toggleWishlist,
      isWishlisted,
    }),
    [
      addToCart,
      cartCount,
      cartItems,
      clearCart,
      isWishlisted,
      removeFromCart,
      subtotal,
      toggleWishlist,
      updateQuantity,
      wishlist,
    ]
  );

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  );
};
